/**
 * 화면 고정(screen-space) 유틸.
 *
 * 이미지 레이어는 zoom transform(translate + scale)을 쓰고,
 * 주석/라벨/선 두께는 "화면 크기 일정"을 원할 때 사용한다.
 */

/** ZoomableImageViewport 가 루트에 심는 CSS 변수명 */
export const ZOOM_K_CSS_VAR = '--zv-zoom-k';

/**
 * 부모에 CSS scale(k)가 있을 때, 화면상 길이를 base로 유지하려면 SVG/CSS 길이를 base/k 로 둔다.
 * @param {number} base
 * @param {number} zoomK
 * @returns {number}
 */
export function screenConstantLength(base, zoomK) {
  const b = Number(base);
  const k = Number(zoomK);
  if (!Number.isFinite(b)) {
    return 0;
  }
  if (!Number.isFinite(k) || k <= 0) {
    return b;
  }
  return b / k;
}

/**
 * 이미지 픽셀 좌표 → 뷰포트(화면) 좌표
 * @param {number} imageX
 * @param {number} imageY
 * @param {{ x: number, y: number, k: number }} transform
 */
export function imageToScreen(imageX, imageY, transform) {
  const t = transform || { x: 0, y: 0, k: 1 };
  const k = Number(t.k) > 0 ? Number(t.k) : 1;
  return {
    x: (Number(t.x) || 0) + k * (Number(imageX) || 0),
    y: (Number(t.y) || 0) + k * (Number(imageY) || 0)
  };
}

/**
 * 뷰포트(화면) 좌표 → 이미지 픽셀 좌표
 * @param {number} screenX
 * @param {number} screenY
 * @param {{ x: number, y: number, k: number }} transform
 */
export function screenToImage(screenX, screenY, transform) {
  const t = transform || { x: 0, y: 0, k: 1 };
  const k = Number(t.k) > 0 ? Number(t.k) : 1;
  return {
    x: ((Number(screenX) || 0) - (Number(t.x) || 0)) / k,
    y: ((Number(screenY) || 0) - (Number(t.y) || 0)) / k
  };
}

/**
 * CSS 커스텀 프로퍼티 맵 (뷰포트 루트에 바인딩)
 * @param {number} zoomK
 * @returns {Record<string, string>}
 */
export function zoomCssVars(zoomK) {
  const k = Number(zoomK) > 0 ? Number(zoomK) : 1;
  return {
    [ZOOM_K_CSS_VAR]: String(k)
  };
}

/**
 * 확대된 레이어 안의 stroke-width 에 쓸 값 (화면상 basePx 유지)
 * CSS 예: stroke-width: calc(1.1px / var(--zv-zoom-k, 1));
 * @param {number} basePx
 * @returns {string}
 */
export function screenStrokeWidthCss(basePx = 1.1) {
  const b = Number(basePx);
  const safe = Number.isFinite(b) ? b : 1.1;
  return `calc(${safe}px / var(${ZOOM_K_CSS_VAR}, 1))`;
}

/**
 * 오버레이를 rect 옆에 배치. 뷰포트 밖으로 나가면 반대편으로 뒤집음.
 * @param {{
 *   rectScreen: { x: number, y: number, w: number, h: number },
 *   overlaySize: { w: number, h: number },
 *   viewport: { w: number, h: number },
 *   gap?: number
 * }} args
 * @returns {{ left: number, top: number, flippedX: boolean, flippedY: boolean }}
 */
export function placeOverlayBesideRect({
  rectScreen,
  overlaySize,
  viewport,
  gap = 8
}) {
  const rw = Number(rectScreen?.w) || 0;
  const rh = Number(rectScreen?.h) || 0;
  const rx = Number(rectScreen?.x) || 0;
  const ry = Number(rectScreen?.y) || 0;
  const ow = Math.max(1, Number(overlaySize?.w) || 1);
  const oh = Math.max(1, Number(overlaySize?.h) || 1);
  const vw = Math.max(1, Number(viewport?.w) || 1);
  const vh = Math.max(1, Number(viewport?.h) || 1);
  const g = Number.isFinite(gap) ? gap : 8;

  let flippedX = false;
  let flippedY = false;

  // 기본: 오른쪽
  let left = rx + rw + g;
  if (left + ow > vw) {
    left = rx - g - ow;
    flippedX = true;
  }

  // 기본: 박스 상단 정렬
  let top = ry;
  if (top + oh > vh) {
    top = ry + rh - oh;
    flippedY = true;
  }

  left = Math.max(0, Math.min(left, vw - ow));
  top = Math.max(0, Math.min(top, vh - oh));

  return { left, top, flippedX, flippedY };
}
